import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { LayoutDashboard, Users, GraduationCap, CalendarDays, ClipboardCheck, BookOpen, Award, HeartPulse, ScrollText, Bell, BarChart3, UserCog, History, LogOut, Menu, KanbanSquare, X, BookOpenCheck, ListChecks, Hourglass, FileCheck2, Settings2, UserX, Rocket, Sparkles, ShieldCheck, KeyRound, HeartHandshake } from "lucide-react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../lib/i18n";
import { StatusBadge } from "../common/Ui";
import { HeaderBar } from "./HeaderBar";
import { GlobalSearch } from "./GlobalSearch";
import { ThemeToggle } from "./ThemeToggle";

// Cluster diurutkan berdasarkan frekuensi pemakaian: harian → mingguan → per siklus → jarang
const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, perm: null, section: "daily" },
  { to: "/assessment", key: "assessment", icon: Award, perm: "scores:read", section: "daily" },
  { to: "/attendance", key: "attendance", icon: ClipboardCheck, perm: "attendance:read", section: "daily" },
  { to: "/attitude", key: "attitude", icon: HeartPulse, perm: "attitude:read", section: "daily" },
  { to: "/schedule", key: "schedule", icon: CalendarDays, perm: "schedule:read", section: "daily" },
  { to: "/students", key: "students", icon: Users, perm: "students:read", section: "people" },
  { to: "/users", key: "teachers", icon: UserCog, perm: "users:read", section: "people" },
  { to: "/kanban", key: "kanban", icon: KanbanSquare, perm: "students:read", section: "people" },
  { to: "/reports", key: "reports", icon: BarChart3, perm: "reports:read", section: "people" },
  { to: "/certificates", key: "certificates", icon: ScrollText, perm: "certificates:read", section: "people" },
  { to: "/alumni", key: "alumni", icon: GraduationCap, perm: "students:read", section: "people" },
  { to: "/dropped", key: "dropped", icon: UserX, perm: "students:read", section: "people" },
  { to: "/hearing", key: "hearing", icon: HeartHandshake, perm: "hearing:read", section: "people" },
  { to: "/setup", key: "setup", icon: Rocket, perm: "academics:write", section: "cycle" },
  { to: "/modules", key: "program", icon: BookOpen, perm: "lms:read", section: "cycle" },
  { to: "/planning", key: "planning", icon: Hourglass, perm: "academics:write", section: "cycle" },
  { to: "/curriculum", key: "curriculum", icon: BookOpenCheck, perm: "lms:read", section: "cycle" },
  { to: "/quizzes", key: "quizzes", icon: ListChecks, perm: null, section: "cycle" },
  { to: "/alerts", key: "alerts", icon: Bell, perm: "alerts:read", section: "insight" },
  { to: "/requests", key: "requests", icon: FileCheck2, perm: "requests:read", section: "insight" },
  { to: "/academics", key: "academics", icon: GraduationCap, perm: "academics:write", section: "admin" },
  { to: "/access", key: "access", icon: ShieldCheck, perm: "users:write", section: "admin" },
  { to: "/audit", key: "audit", icon: History, perm: "audit:read", section: "admin" },
  { to: "/settings", key: "settings", icon: Settings2, perm: null, role: "super_admin", section: "admin" },
  { to: "/scoring", key: "scoring", icon: ListChecks, perm: "scores:read", section: "admin", hidden: true },
  { to: "/guide", key: "guide", icon: BookOpenCheck, perm: null, section: "help" },
  { to: "/showcase", key: "showcase", icon: Sparkles, perm: null, section: "help" },
];

const NAV_QUIZ_ROLES = ["super_admin", "academic_admin", "teacher", "student", "manager"];

const LANGS = [["id", "ID"], ["en", "EN"], ["jp", "日本語"]];
function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold dark:bg-slate-800 dark:border-slate-700" data-testid="language-toggle" title="Bahasa / Language / 言語">
      {LANGS.map(([l, label]) => (
        <button key={l} data-testid={`language-toggle-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full transition-colors ${lang === l ? (l === "jp" ? "bg-red-600 text-white" : "bg-slate-900 text-white") : "text-slate-500 hover:text-slate-900 dark:text-slate-300"}`}>{label}</button>
      ))}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, can } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const [pendingDaily, setPendingDaily] = useState(0);
  useEffect(() => {
    if (!can("scores:read")) return;
    const load = () => api.get("/daily-scores/pending").then((r) => setPendingDaily(r.data.dates || 0)).catch(() => {});
    load();
    window.addEventListener("daily-pending-changed", load);
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { window.removeEventListener("daily-pending-changed", load); clearInterval(t); };
  }, []); // eslint-disable-line
  const items = NAV.filter((n) => !n.hidden && (!n.perm || can(n.perm)) && (!n.role || user.role === n.role) && (n.key !== "quizzes" || NAV_QUIZ_ROLES.includes(user.role)));

  const Sidebar = (
    <aside className="w-64 shrink-0 h-full bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200">
        <img src="/logo-yumanabu.jpg" alt="YLMS" className="h-9 w-9 rounded-md object-contain bg-white border border-slate-200" />
        <div className="leading-tight">
          <p className="font-heading font-bold text-slate-900 text-sm">{t("appName")}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-tight">Yutaka Learning<br />Management System</p>
        </div>
        <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-0.5">
        {items.map((n, i) => (
          <div key={n.to}>
            {(i === 0 || items[i - 1].section !== n.section) && (
              <div className={i === 0 ? "pt-1" : "mt-3 pt-3 border-t border-slate-200 dark:border-slate-700"}>
                <p className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200" data-testid={`nav-section-${n.section}`}>{t(`navSection.${n.section}`)}</p>
              </div>)}
            <NavLink to={n.to} end={n.to === "/"} data-testid={`nav-${n.key}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
              <n.icon className="h-4 w-4" /> <span>{t(n.key)}</span>
              {n.key === "assessment" && pendingDaily > 0 && <span data-testid="nav-assessment-badge" title={`${pendingDaily} tanggal nilai harian belum close`} className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{pendingDaily > 99 ? "99+" : pendingDaily}</span>}
            </NavLink>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-[color:var(--brand-blue)] text-white grid place-items-center font-semibold text-sm">{user.name?.[0]}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">{user.name}</p>
            <StatusBadge value={user.role} testId="current-user-role" />
          </div>
          <button data-testid="change-password-button" onClick={() => setCpOpen(true)} className="text-slate-400 hover:text-[color:var(--brand-blue)] transition-colors" title="Ganti kata sandi">
            <KeyRound className="h-4 w-4" />
          </button>
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }} className="text-slate-400 hover:text-red-600 transition-colors" title={t("logout")}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
    </aside>
  );

  return (
    <div className="h-screen flex bg-[#F8FAFC] text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="hidden lg:block h-full">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header data-testid="app-header" className="h-16 sticky top-0 z-30 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-slate-200 bg-white/90 backdrop-blur-md shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden" data-testid="mobile-menu-button" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <p className="text-sm text-slate-500 hidden xl:block truncate">{t("welcome")}, <span className="font-medium text-slate-900">{user.name}</span></p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3"><GlobalSearch /><HeaderBar /><ThemeToggle /><LangToggle /></div>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur-md grid grid-cols-5" data-testid="mobile-bottom-nav">
          {items.filter((n) => ["dashboard", "students", "assessment", "reports"].includes(n.key)).slice(0, 4).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-nav-${n.key}`} className={({ isActive }) => `relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium ${isActive ? "text-[color:var(--brand-blue)]" : "text-slate-500"}`}>
              {({ isActive }) => <><span className={`grid h-7 w-10 place-items-center rounded-full ${isActive ? "bg-sky-50" : ""}`}><n.icon className="h-4 w-4" /></span><span className="truncate max-w-[64px]">{t(n.key)}</span>{n.key === "assessment" && pendingDaily > 0 && <span className="absolute top-1 right-1/2 translate-x-4 h-2 w-2 rounded-full bg-red-600" />}</>}
            </NavLink>
          ))}
          <button type="button" data-testid="bottom-nav-menu" onClick={() => setOpen(true)} className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-slate-500"><span className="grid h-7 w-10 place-items-center"><Menu className="h-4 w-4" /></span>Menu</button>
        </nav>
      </div>
    </div>
  );
}
